/**
 * useTenantConfig Hooks
 * React Query hooks for tenant configuration
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import tenantConfigService from '../services/tenantConfigService';

/**
 * Hook for full tenant config
 */
export function useTenantConfig(tenantId) {
  return useQuery({
    queryKey: ['tenant-config', tenantId],
    queryFn: () => tenantConfigService.getConfig(tenantId),
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

/**
 * Hook for updating tenant config
 */
export function useUpdateTenantConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, updates }) => tenantConfigService.updateConfig(tenantId, updates),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', variables.tenantId] });
    },
  });
}

/**
 * Hook for feature flags only
 */
export function useTenantFeatureFlags(tenantId) {
  return useQuery({
    queryKey: ['tenant-feature-flags', tenantId],
    queryFn: () => tenantConfigService.getFeatureFlags(tenantId),
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook for updating feature flags
 */
export function useUpdateFeatureFlags() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, flags }) => tenantConfigService.updateFeatureFlags(tenantId, flags),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', variables.tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-feature-flags', variables.tenantId] });
    },
  });
}

/**
 * Hook for limits only
 */
export function useTenantLimits(tenantId) {
  return useQuery({
    queryKey: ['tenant-limits', tenantId],
    queryFn: () => tenantConfigService.getLimits(tenantId),
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook for updating limits
 */
export function useUpdateLimits() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, limits }) => tenantConfigService.updateLimits(tenantId, limits),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', variables.tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-limits', variables.tenantId] });
    },
  });
}

/**
 * Hook for access controls only
 */
export function useTenantAccess(tenantId) {
  return useQuery({
    queryKey: ['tenant-access', tenantId],
    queryFn: () => tenantConfigService.getAccess(tenantId),
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000,
  });
}

/**
 * Hook for updating access controls
 */
export function useUpdateAccess() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ tenantId, access }) => tenantConfigService.updateAccess(tenantId, access),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['tenant-config', variables.tenantId] });
      queryClient.invalidateQueries({ queryKey: ['tenant-access', variables.tenantId] });
    },
  });
}
